import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { PpwLocalStorageService } from '../../app/common/services/ppwlocalstorage.service'
import { Employee, EmployeePaginatedList, EmployeeSearchCriteria } from '../models/employee.model'
import { BaseService } from './base.service'

@Injectable({
    providedIn: 'root'
})
export class EmployeeService extends BaseService {
    private reload$$: BehaviorSubject<void> = new BehaviorSubject<void>(void 0)
    get reload$(): Observable<void> {
        return this.reload$$.asObservable()
    }

    constructor(private readonly ppwLocalStorageService: PpwLocalStorageService, readonly http: HttpClient) {
        super(http)
    }

    async getEmployees(employeeSearchCriteria: EmployeeSearchCriteria): Promise<EmployeePaginatedList> {
        const preferences = this.ppwLocalStorageService.getStoredData(
            this.ppwLocalStorageService.STORAGE_KEY_PROFILE_PREFERENCES
        )
        employeeSearchCriteria.includeArchived = preferences && preferences.showArchivedData

        return this.$http
            .get<EmployeePaginatedList>(this.uri, { params: employeeSearchCriteria.httpParams(), observe: 'response' })
            .pipe(
                map((response) => EmployeePaginatedList.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async getAllEmployees(): Promise<EmployeePaginatedList> {
        const searchCriteria = new EmployeeSearchCriteria()
        searchCriteria.pageSize = 100000
        searchCriteria.page = 1

        return this.getEmployees(searchCriteria)
    }

    async saveEmployee(employee: Employee): Promise<Employee> {
        if (employee.id) {
            return this.updateEmployee(employee)
        }

        return this.createEmployee(employee)
    }

    async archiveEmployee(employee: Employee): Promise<Employee> {
        const uri = `${this.uri}/${employee.id}`

        return this.$http
            .delete<Employee>(uri, { observe: 'response' })
            .pipe(
                map((response) => Employee.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    markCurrentListDirty(): void {
        this.reload$$.next()
    }
    private get uri(): string {
        return `${this.baseUri}/Employees`
    }

    private createEmployee(employee: Employee): Promise<Employee> {
        return this.$http
            .post<Employee>(this.uri, employee, { observe: 'response' })
            .pipe(
                map((response) => Employee.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    private updateEmployee(employee: Employee): Promise<Employee> {
        return this.$http
            .put<Employee>(`${this.uri}/${employee.id}`, employee, { observe: 'response' })
            .pipe(
                map((response) => Employee.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }
}
